<template>
    <Card v-loading="pageLoading" class="double-authentication-page">
        <div class="double-authentication-page__introduction">
            <div class="text-container">
                <p class="semibold fs-3 mt-0 mb-2">
                    {{
                        $l10n.__(
                            'La double authentification permet de renforcer significativement la sécurité de votre compte Sellsy.',
                        )
                    }}
                </p>
                <p class="fs-3 mt-0 mb-2">
                    {{
                        $l10n.__(
                            "Si nous remarquons une tentative de connexion à partir d'un appareil non reconnu, nous vous demanderons un code de sécurité en plus de votre mot de passe. \nVous recevrez ce code via la méthode d'authentification configurée.",
                        )
                    }}
                </p>
            </div>
            <el-tooltip placement="bottom-end" :disabled="!corporationEnabled" popperClass="twoFA">
                <template #content>
                    <div>
                        {{ $l10n.__('La double authentification est obligatoire') }}<br />
                        {{ $l10n.__('et ne peut être désactivée.') }}
                    </div>
                </template>
                <div>
                    <CompassButton
                        v-if="isActivated"
                        class="btn-danger"
                        :label="
                            $t.__(
                                'pages.settings.doubleAuthentication.doubleAuthentication.disable',
                            )
                        "
                        type="primary-destructive"
                        :disabled="isLoading || corporationEnabled"
                        @click.prevent="showConfirmation"
                    />
                </div>
            </el-tooltip>
        </div>

        <DoubleAuthenticationStatus :active="isActivated" />

        <template v-if="!hasActiveProvider">
            <h3 class="mt-0 mb-2">{{ $l10n.__("Méthode d'authentification principale") }}</h3>
            <p class="fs-3 mt-0 mb-2">
                {{
                    $l10n.__(
                        "Pour démarrer la configuration de la double authentification, sélectionnez une méthode d'authentification principale",
                    )
                }}
            </p>
            <div class="available-providers" data-test="availableProviders">
                <AvailableProvider2FA
                    v-for="provider in availableProviders"
                    :key="provider.id"
                    :provider="provider"
                />
            </div>
        </template>

        <template v-else>
            <h3 class="mt-0 mb-2">{{ $l10n.__("Vos méthodes d'authentification") }}</h3>

            <table class="custom-table" data-test="providersList">
                <tbody>
                    <tr v-for="provider in activatedProviders" :key="provider.id">
                        <td class="bold">
                            {{
                                provider.primary
                                    ? $l10n.__("Méthode d'authentification principale")
                                    : $l10n.__("Méthode d'authentification secondaire ")
                            }}
                        </td>
                        <td class="gray-60">{{ provider.type }}</td>
                        <td>
                            <div class="provider-type">
                                <p class="provider-type__text m-0 bold">
                                    {{ provider.label }}
                                </p>
                            </div>
                        </td>
                        <td>
                            <CompassButton
                                :label="
                                    $t.__(
                                        'pages.settings.doubleAuthentication.doubleAuthentication.disable',
                                    )
                                "
                                type="secondary-destructive"
                                @click="deactivateProvider(provider)"
                            />
                        </td>
                    </tr>
                </tbody>
            </table>

            <template v-if="hasBackupCodes">
                <h4 class="mt-3 mb-2 fs-4">{{ $l10n.__('Codes de secours') }}</h4>

                <div class="flex-row">
                    <div class="text-container">
                        <p class="mt-0 mb-1 fs-3">
                            {{
                                $l10n.__(
                                    'Si vous perdez votre appareil alors que la double authentification est activée, vous aurez besoin des codes de secours pour vous connecter à votre compte. Conservez-les en lieu sûr, de préférence avec vous.',
                                )
                            }}
                        </p>
                        <p class="m-0 gray-60 fs-3">
                            {{
                                $l10n.__(
                                    "Chaque code de secours est à usage unique, mais n'hésitez pas à les utiliser car vous pouvez en générer des nouveaux.",
                                )
                            }}
                        </p>
                    </div>
                    <div class="code-actions">
                        <CompassButton
                            :label="
                                $t.__(
                                    'pages.settings.doubleAuthentication.doubleAuthentication.view_codes',
                                )
                            "
                            type="secondary"
                            data-test="btnBackupCodes"
                            @click="showBackupCodes"
                        />
                        <div class="mt-2 gray-60 semibold aright" data-test="unusedBackupCodes">
                            {{ unusedBackupCodes }}
                        </div>
                    </div>
                </div>
            </template>

            <template v-if="secondaryProvidersAvailable">
                <h3 class="mt-6 mb-2">{{ $l10n.__("Méthodes d'authentification secondaire") }}</h3>
                <div class="text-container">
                    <p class="fs-3 mt-0 mb-2">
                        {{
                            $l10n.__(
                                "Configurer une méthode secondaire vous permet de vous connecter à votre compte même si vous n'avez pas accès à votre méthode principale sur le moment.",
                            )
                        }}
                    </p>
                </div>
                <div class="available-providers">
                    <AvailableProvider2FA
                        v-for="provider in secondaryProviders"
                        :key="provider.id"
                        :provider="provider"
                    />
                </div>
            </template>

            <template v-if="hasTrustedDevices">
                <h3 class="mt-6 mb-2">{{ $l10n.__('Appareils de confiance') }}</h3>
                <div class="text-container">
                    <p class="fs-3 mt-0 mb-1">
                        {{
                            $l10n.__(
                                "Aucun code de sécurité ne vous sera demandé pendant 30 jours lorsque vous vous connectez à partir d'un appareil de confiance.",
                            )
                        }}
                    </p>
                    <p class="gray-60 fs-3 mt-0 mb-3">
                        {{
                            $l10n.__(
                                "Si vous ne reconnaissez pas un appareil de confiance ou s'il n'est plus en votre possession, supprimez-le immédiatement.",
                            )
                        }}
                    </p>
                </div>

                <table class="custom-table" data-test="trustedDevices">
                    <tbody>
                        <tr v-for="trustedDevice in trustedDevices" :key="trustedDevice.id">
                            <td class="bold">
                                {{ formatDeviceType(trustedDevice.device) }}
                            </td>
                            <td>
                                {{ trustedDevice.os }}
                            </td>
                            <td>
                                {{ trustedDevice.browser }}
                            </td>
                            <td>
                                <div>
                                    {{ $l10n.__('Première connexion :') }}
                                    {{ formatCreationDate(trustedDevice.created_at) }}
                                </div>
                                <div class="gray-60 fs-2">
                                    {{ $l10n.__('Dernière activité :') }}
                                    {{ formatLastLoginDate(trustedDevice.last_login_at) }}
                                </div>
                            </td>
                            <td>
                                <CompassButton
                                    :label="
                                        $t.__(
                                            'pages.settings.doubleAuthentication.doubleAuthentication.no_longer_trust',
                                        )
                                    "
                                    type="secondary-destructive"
                                    data-test="btnRemoveTrustedDevice"
                                    @click="removeTrustedDevice(trustedDevice)"
                                />
                            </td>
                        </tr>
                    </tbody>
                </table>
            </template>
        </template>
    </Card>
</template>

<script>
import Card from 'vcomponents/common/card/Card.vue';
import DoubleAuthenticationStatus from 'vcomponents/doubleAuthenticationStatus/DoubleAuthenticationStatus.vue';
import { providersInformation } from 'Config/authenticated/dictionary/providers2FA';
import AvailableProvider2FA from 'vcomponents/availableProvider2FA/availableProvider2FA.vue';
import { createNamespacedHelpers } from 'vuex';
const popupPanelMapActions = createNamespacedHelpers('popupPanel').mapActions;

export default {
    components: {
        Card,
        DoubleAuthenticationStatus,
        AvailableProvider2FA,
    },
    data() {
        return {
            isActivated: false,
            corporationEnabled: false,
            providers: [],
            availableProviders: [],
            backupCodes: [],
            trustedDevices: [],
            isLoading: false,
            pageLoading: true,
        };
    },
    computed: {
        hasActiveProvider() {
            return this.providers.length > 0;
        },
        secondaryProvidersAvailable() {
            return this.availableProviders.length > 1 && this.providers.length === 1;
        },
        secondaryProviders() {
            return this.availableProviders.filter(
                (item) => item.provider !== this.availableProviders[0].provider,
            );
        },
        hasTrustedDevices() {
            return this.trustedDevices.length > 0;
        },
        activatedProviders() {
            return this.providers.sort((a, b) => Number(b.primary) - Number(a.primary));
        },
        unusedBackupCodes() {
            const total = this.backupCodes.filter((item) => !item.used_at).length;
            return this.$l10n.n__('%s code restant', '%s codes restants', total, total);
        },
        hasBackupCodes() {
            return this.backupCodes.length > 0;
        },
    },
    created() {
        this.get2FAInfo();
        EventBus.on('2fa:updateUserSettings', this.get2FAInfo);
        EventBus.on('2fa:closeDeactivationPopup', this.closeDeactivationPopup);
        EventBus.on('2fa:updateBackupCodes', this.updateBackupCodes);
    },
    unmounted() {
        EventBus.off('2fa:updateUserSettings', this.get2FAInfo);
        EventBus.off('2fa:closeDeactivationPopup', this.closeDeactivationPopup);
        EventBus.off('2fa:updateBackupCodes', this.updateBackupCodes);
    },
    mounted() {
        this.$nextTick(() => {
            this.pageLoading = false;
        });
    },
    methods: {
        ...popupPanelMapActions(['open', 'feedProps']),
        get2FAInfo() {
            this.pageLoading = true;
            ApiClient.get('/settings/two-factor-authentication/user').then((response) => {
                this.isActivated = response.data.enabled;
                if (response.data.providers.length > 0) {
                    this.buildProviders(response.data.providers);
                } else {
                    this.providers = [];
                }
                this.buildProviders(response.data.available_providers, true);
                this.backupCodes = response.data.backup_codes;
                this.corporationEnabled = response.data.corporation_enabled;
                this.trustedDevices = response.data.trusted_devices;
                this.pageLoading = false;
            });
        },
        buildProviders(receivedProviders, isAvailableProviders) {
            const providersWithInfo = receivedProviders.map((provider) => {
                return {
                    ...provider,
                    ...providersInformation.find((item) => item.provider === provider.provider),
                };
            });
            if (isAvailableProviders) {
                this.availableProviders = providersWithInfo;
            } else {
                this.providers = providersWithInfo;
            }
        },
        showConfirmation() {
            this.isLoading = true;
            this.open({
                key: 'deactivate2FA',
                props: {
                    provider: null,
                },
            });
        },
        showBackupCodes() {
            this.open({
                key: 'backupCodes2FA',
                props: {
                    backupCodes: this.backupCodes,
                },
            });
        },
        updateBackupCodes() {
            ApiClient.get('/settings/two-factor-authentication/user').then((response) => {
                this.backupCodes = response.data.backup_codes;
                this.feedProps({
                    key: 'backupCodes2FA',
                    props: {
                        backupCodes: response.data.backup_codes,
                    },
                });
            });
        },
        deactivateProvider(provider) {
            this.open({
                key: 'deactivate2FA',
                props: {
                    provider,
                    requiredFromCorp: this.corporationEnabled,
                    providers: this.providers,
                },
            });
        },
        formatDeviceType(deviceType) {
            switch (deviceType) {
                case 'desktop':
                    return this.$l10n.__('Ordinateur');
                case 'smartphone':
                    return this.$l10n.__('Smartphone');
                case 'tablet':
                    return this.$l10n.__('Tablette');
                default:
                    return this.$l10n.__('Autre');
            }
        },
        formatCreationDate(date) {
            return moment(date).format('ll');
        },
        formatLastLoginDate(date) {
            if (!moment(date).isBefore(moment().subtract(1, 'days'))) {
                return moment(date).fromNow();
            }
            return moment(date).format('lll');
        },
        removeTrustedDevice(trustedDevice) {
            this.open({
                key: 'removeTrustedDevice',
                props: {
                    trustedDevice: {
                        ...trustedDevice,
                        device: this.formatDeviceType(trustedDevice.device),
                    },
                },
            });
        },
        closeDeactivationPopup() {
            this.isLoading = false;
        },
    },
};
</script>

<style lang="scss" scoped>
@use '~@sellsy/styleguide/src/_variables' as *;

.double-authentication-page {
    &__introduction {
        display: flex;
        gap: var(--spacing-3);
        justify-content: space-between;
    }

    .text-container {
        max-width: 800px;
    }

    .available-providers {
        display: flex;
        margin: 16px 0 0;
        width: 100%;
    }

    .custom-table {
        border: 1px solid $gray-40;
        border-radius: 4px;
        border-spacing: 0;
        width: 100%;

        tr {
            &:not(:last-child) {
                td {
                    border-bottom: 1px solid $gray-40;
                }
            }
        }

        td {
            font-size: 14px;
            height: 56px;
            padding: 0 16px;
            vertical-align: middle;

            &:last-child {
                text-align: end;
            }
        }

        .provider-type {
            align-items: center;
            display: flex;

            &__icon {
                height: 24px;
                margin: 0 8px 0 0;
                width: 24px;
            }
        }

        .btn {
            background: none;
            border-color: $gray-40;
            color: $gray-100;

            &:hover {
                border-color: $gray-100;
            }
        }
    }

    .code-actions {
        flex-shrink: 0;
        margin-left: auto;
        padding: 0 0 0 16px;
    }

    .is-current-device {
        &__icon {
            display: inline-block;
            margin-right: 12px;
            vertical-align: middle;
            svg {
                fill: $primary;
            }
        }

        &__text {
            display: inline-block;
            line-height: 20px;
        }
    }
}
</style>
